/**
 * Service Desk → Tickets — the drill-in page the dashboard navigates to
 * when you click a metric. Mirrors the Application Support inbox: a
 * master-detail layout (ticket list on the left, the selected ticket's
 * detail rendered in-app on the right) rather than a flat list that
 * bounces out to Freshservice.
 *
 * Reads ?f=<filter> (set by the dashboard) to choose the slice, and
 * ?t=<ticket_id> to track the open ticket so a selection is shareable +
 * survives refresh. No "Promote to Requested" action here — that's an
 * intake-queue concern; this is a desk-wide read/triage view.
 */
import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2, Paperclip } from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import { cn } from "@/lib/cn";
import { attachmentHref } from "@/lib/attachmentHref";
import {
  useServiceDeskOverview,
  useFreshserviceStatus,
  useFreshserviceTicketDetail,
  type ServiceDeskTicket,
} from "@/hooks/useFreshservice";
import {
  parseFilter,
  filterMatches,
  filterLabel,
  daysAgo,
  PRIORITY_COLOR,
} from "@/lib/serviceDeskFilter";

export function ServiceDeskTickets() {
  const statusQ = useFreshserviceStatus();
  const isConfigured = statusQ.data?.configured === true;
  const overview = useServiceDeskOverview(isConfigured);
  const [searchParams, setSearchParams] = useSearchParams();

  const tickets = overview.data?.tickets ?? [];
  const priorityLabels = overview.data?.priority_labels ?? {
    1: "Low", 2: "Medium", 3: "High", 4: "Highest",
  };
  const filter = parseFilter(searchParams.get("f"));

  const matching = useMemo(
    () =>
      tickets
        .filter((t) => filterMatches(t, filter))
        .sort((a, b) => ((a.created_at ?? "") < (b.created_at ?? "") ? -1 : 1)),
    [tickets, filter],
  );

  const selectedId = searchParams.get("t");
  const selected = useMemo(
    () => matching.find((t) => String(t.ticket_id) === selectedId) ?? null,
    [matching, selectedId],
  );

  // Auto-select the first ticket once the list resolves so the detail
  // pane is never empty on arrival (matches the App Support inbox,
  // which lands with the first ticket open).
  useEffect(() => {
    if (matching.length === 0) return;
    if (selected) return;
    const next = new URLSearchParams(searchParams);
    next.set("t", String(matching[0].ticket_id));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matching, selected]);

  const selectTicket = (id: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("t", String(id));
    setSearchParams(next, { replace: true });
  };

  const heading = filterLabel(filter, priorityLabels);

  return (
    <>
      <TopBar title={heading} subtitle="Service Desk · unresolved tickets">
        <Link
          to="/service-desk"
          className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-3 hover:bg-raised"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Dashboard
        </Link>
      </TopBar>

      <div className="p-8">
        {!isConfigured ? (
          <Notice>Freshservice isn't configured.</Notice>
        ) : overview.isLoading ? (
          <Notice>
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading tickets…
          </Notice>
        ) : overview.isError ? (
          <Notice tone="error">
            Couldn't load tickets. {(overview.error as any)?.response?.data?.detail ?? ""}
          </Notice>
        ) : matching.length === 0 ? (
          <Notice>No tickets match this filter.</Notice>
        ) : (
          <div className="grid gap-4 lg:grid-cols-[minmax(320px,420px)_1fr]">
            {/* Master — ticket list */}
            <div className="overflow-hidden rounded-xl bg-surface ring-1 ring-line">
              <div className="flex items-center justify-between border-b border-line-soft px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-ink">
                  Tickets
                  <span className="rounded-full bg-raised px-2 py-0.5 text-[10px] font-bold tabular-nums text-ink-3">
                    {matching.length}
                  </span>
                </div>
              </div>
              <div className="max-h-[calc(100vh-220px)] divide-y divide-line-soft overflow-y-auto">
                {matching.map((t) => (
                  <TicketRow
                    key={t.ticket_id}
                    ticket={t}
                    selected={String(t.ticket_id) === selectedId}
                    onClick={() => selectTicket(t.ticket_id)}
                  />
                ))}
              </div>
            </div>

            {/* Detail */}
            <div className="overflow-hidden rounded-xl bg-surface ring-1 ring-line">
              {selected ? (
                <TicketDetailPane ticket={selected} />
              ) : (
                <div className="flex h-full items-center justify-center p-12 text-sm text-ink-subtle">
                  Select a ticket to view its detail.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Master row
// ---------------------------------------------------------------------------

function TicketRow({
  ticket: t, selected, onClick,
}: {
  ticket: ServiceDeskTicket;
  selected: boolean;
  onClick: () => void;
}) {
  const age = daysAgo(t.created_at);
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors",
        selected ? "bg-brand-navy-50 dark:bg-brand-navy-500/10 ring-1 ring-inset ring-brand-navy-200 dark:ring-brand-navy-500/30" : "hover:bg-raised",
      )}
    >
      <span
        className={cn(
          "mt-0.5 w-10 shrink-0 rounded-full px-2 py-0.5 text-center text-[10px] font-bold tabular-nums",
          (age ?? 0) >= 90 ? "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300"
            : (age ?? 0) >= 30 ? "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300"
              : "bg-raised text-ink-3",
        )}
      >
        {age != null ? `${age}d` : "—"}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-[10px] text-ink-subtle">
          <span className="font-mono">#{t.ticket_id}</span>
          {t.group_name && <span className="truncate">· {t.group_name}</span>}
        </div>
        <div className="mt-0.5 truncate text-sm font-medium text-ink">{t.subject}</div>
      </div>
      <span className={cn(
        "mt-0.5 shrink-0 text-[10px] font-semibold",
        PRIORITY_COLOR[t.priority]?.text ?? "text-ink-muted",
      )}>
        {t.priority_label}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Detail pane — fetches the full ticket via the same endpoint the App
// Support inbox uses, renders in-app.
// ---------------------------------------------------------------------------

function TicketDetailPane({ ticket }: { ticket: ServiceDeskTicket }) {
  const detailQ = useFreshserviceTicketDetail(ticket.ticket_id);
  const detail = detailQ.data;
  const description = detail?.description_text ?? "";
  const requesterName = detail?.requester?.name ?? null;
  const requesterEmail = detail?.requester?.email ?? null;
  const conversations = detail?.conversations ?? [];

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-line-soft px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[11px] text-ink-muted">
              <span className="font-mono text-ink-subtle">#{ticket.ticket_id}</span>
              {ticket.created_at && <><span>·</span><span>{fmtDate(ticket.created_at)}</span></>}
              {ticket.group_name && <><span>·</span><span>{ticket.group_name}</span></>}
            </div>
            <h2 className="mt-1 text-base font-semibold leading-snug text-ink">
              {ticket.subject}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                ticket.priority >= 4 ? "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300"
                  : ticket.priority === 3 ? "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300"
                    : ticket.priority === 2 ? "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300"
                      : "bg-raised text-ink-3",
              )}>
                {ticket.priority_label}
              </span>
              <span className="rounded-full bg-raised px-2 py-0.5 text-[10px] font-medium text-ink-3">
                {ticket.status_label}
              </span>
              {ticket.type && (
                <span className="rounded-full bg-raised px-2 py-0.5 text-[10px] font-medium text-ink-3">
                  {ticket.type}
                </span>
              )}
              {ticket.agent_name ? (
                <span className="rounded-full bg-raised px-2 py-0.5 text-[10px] font-medium text-ink-3">
                  {ticket.agent_name}
                </span>
              ) : (
                <span className="rounded-full bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/30">
                  Unassigned
                </span>
              )}
            </div>
          </div>
          <a
            href={ticket.url}
            target="_blank"
            rel="noreferrer"
            className="flex shrink-0 items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised"
            title="Open in Freshservice"
          >
            <ExternalLink className="h-3 w-3" /> Freshservice
          </a>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {detailQ.isLoading ? (
          <div className="py-8 text-center text-sm text-ink-subtle">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading detail…
          </div>
        ) : (
          <div className="space-y-5">
            {/* Attachments */}
            {detail && detail.attachments.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
                  <Paperclip className="h-3 w-3" />
                  {detail.attachments.length} {detail.attachments.length === 1 ? "Attachment" : "Attachments"}
                </div>
                <div className="flex flex-wrap gap-2">
                  {detail.attachments.map((a) => (
                    <a
                      key={a.id}
                      href={attachmentHref(a.url, {
                        filename: a.name,
                        contentType: a.content_type,
                      })}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-ink-2 hover:border-brand-navy-300 hover:bg-brand-navy-50/40 dark:hover:bg-brand-navy-500/10"
                    >
                      <Paperclip className="h-3 w-3 text-ink-subtle" />
                      {a.name}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Requester */}
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
                Requester
              </div>
              {requesterName || requesterEmail ? (
                <div className="text-sm text-ink-2">
                  {requesterName && <div className="font-medium">{requesterName}</div>}
                  {requesterEmail && <div className="text-xs text-ink-muted">{requesterEmail}</div>}
                </div>
              ) : (
                <div className="text-xs text-ink-subtle">Not available</div>
              )}
            </div>

            {/* Description */}
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
                Description
              </div>
              {description ? (
                <div className="whitespace-pre-wrap rounded-lg bg-canvas px-4 py-3 text-sm leading-relaxed text-ink-2">
                  {description}
                </div>
              ) : (
                <div className="text-xs text-ink-subtle">No description.</div>
              )}
            </div>

            {/* Conversations */}
            {conversations.length > 0 && (
              <div>
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
                  Activity ({conversations.length})
                </div>
                <div className="space-y-3">
                  {conversations.map((c) => (
                    <div key={c.id} className="rounded-lg border border-line-soft px-3 py-2">
                      <div className="flex items-center gap-2 text-[11px] text-ink-muted">
                        <span className="font-medium text-ink-2">{c.author_name ?? "—"}</span>
                        {c.created_at && <span>· {fmtDate(c.created_at)}</span>}
                        {c.is_private && (
                          <span className="rounded-full bg-raised px-1.5 py-0.5 text-[9px] font-semibold uppercase text-ink-muted">
                            Private
                          </span>
                        )}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-ink-3">
                        {c.body_text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Notice({ children, tone }: { children: React.ReactNode; tone?: "error" }) {
  return (
    <div className={cn(
      "rounded-xl p-8 text-center text-sm",
      tone === "error" ? "border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300" : "border border-line bg-surface text-ink-muted",
    )}>
      {children}
    </div>
  );
}

function fmtDate(iso: string): string {
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
